import { ButtonPrimary, ButtonSecondary, ButtonTertiary } from "@nova/component-btn"
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    useIntegrationAuthorizations,
    useUpdateIntegrationAuthorization,
} from "features/data-management/integrations/useIntegrations"
import { useCreateLiveStreamAuthorization } from "./useCreateLiveStreamAuthorization"
import * as Icons from "@nova/component-icons"
import { selectUserID } from "user/profile/selectors"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { useIsEditor } from "tools/hooks/useIsEditor"
import type { IntegrationAuthorizationResponse } from "@nova/data-facade"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { PasswordInput } from "@nova/component-password-input"
import {
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
} from "panels/routing/WorkspaceRoutes"
import { IntegrationType } from "@nova/schemas"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { useRouter } from "pages"
import { ExpandableQRCode } from "features/data-management/workspace/TODO-put-in-package/ExpandableQRCode"
import {
    multistreamUrl,
    ShareInactiveStreamLinkModal,
    useShareLiveStream,
} from "features/multi-stream/ShareLiveStreamModal"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"

export const AllStreamsLinkSettingsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({ title: "All Streams Link", border: true }),
    Content: AllStreamsLinksSettingsContent,
})

export const AllStreamsLinkRedirect = () => {
    const router = useRouter("/workspace/:workspaceId")
    useEffect(() => {
        router.navigate({ path: "/settings/all-streams-link" }, { replace: true })
    }, [router])
    return null
}

function AllStreamsLinksSettingsContent() {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const { data = [], isLoading } = useIntegrationAuthorizations({
        userId,
        workspaceId,
    })
    const liveStreamAuths = data.filter((auth) => auth.integration === IntegrationType.liveStream)
    const auth = liveStreamAuths.at(0) as
        | IntegrationAuthorizationResponse<"empty", IntegrationType.liveStream>
        | undefined
    const [isApplyingChanges, setIsApplyingChanges] = useState(false)
    const [localPassword, setLocalPassword] = useState("")
    useEffect(() => {
        if (!auth) return
        setLocalPassword(auth.settings.passwordEnabled ? (auth.settings.password ?? "") : "")
    }, [auth])
    const isEditor = useIsEditor()

    const { trigger: updateAuth } = useUpdateIntegrationAuthorization()
    const { trigger: createAuth } = useCreateLiveStreamAuthorization()

    const handleUpdateSettings = ({
        passwordEnabled,
        password,
    }: {
        passwordEnabled: boolean
        password: string
    }) => {
        const notify = () => {
            if (!passwordEnabled || !!password)
                dispatch(
                    setGlobalNotificationContext({
                        message: `Your password settings were saved successfully.`,
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 3000,
                    })
                )
        }

        setIsApplyingChanges(true)

        if (!auth) {
            createAuth({
                workspaceId,
                settings: { passwordEnabled, password },
            }).then(() => {
                notify()
                setIsApplyingChanges(false)
            })
            return
        }

        updateAuth({
            workspaceId: auth.workspaceId,
            authUserId: auth.userId,
            id: auth.id,
            fields: {
                settings: { ...auth.settings, passwordEnabled, password },
            },
        }).then(() => {
            notify()
            setIsApplyingChanges(false)
        })
    }

    const confirmationModal = useConfirmationModalContext()
    const { shareLiveStream } = useShareLiveStream()

    if (!workspaceId) return null

    return (
        <div className="flex w-full py-6 max-w-full border-b border-b-gray-700">
            <small className="xs w-[200px] min-w-[200px]">ALL LIVE STREAMS</small>
            {isLoading ? (
                <div className="w-full p-6 flex justify-center items-center">
                    <NovaSpinner width={24} height={24} />
                </div>
            ) : (
                <div className="flex flex-col gap-4 overflow-hidden w-full min-w-0">
                    <div className="flex gap-1 items-center min-w-0">
                        <ButtonPrimary
                            title="Open Streams"
                            RightIcon={Icons.OpenInNew}
                            className="shrink-0 mr-2"
                            onClick={() => {
                                window.open(multistreamUrl({ workspaceId }), "_blank")
                            }}
                        />
                        <ButtonTertiary
                            title="Copy URL"
                            LeftIcon={Icons.Copy}
                            className="shrink-0"
                            onClick={() =>
                                navigator.clipboard
                                    .writeText(multistreamUrl({ workspaceId }))
                                    .then(() =>
                                        dispatch(
                                            setGlobalNotificationContext({
                                                message: "Copied to clipboard",
                                                status: GlobalNotificationStatus.success,
                                                timeoutInMs: 3000,
                                            })
                                        )
                                    )
                            }
                        />
                        {isEditor && (
                            <ButtonSecondary
                                title="Share URL"
                                RightIcon={Icons.Share}
                                className="shrink-0"
                                onClick={() => {
                                    confirmationModal.confirm<{
                                        selectedEmails: string[]
                                        selectedPhoneNumbers: string[]
                                        selectedMissionGroupIds: string[]
                                    }>({
                                        RenderComponent: ({
                                            confirm,
                                            cancel,
                                            isPerformingAsyncConfirmCallback,
                                        }) => (
                                            <ShareInactiveStreamLinkModal
                                                confirm={confirm}
                                                cancel={cancel}
                                                isPerformingAsyncConfirmCallback={
                                                    isPerformingAsyncConfirmCallback
                                                }
                                                type="all-streams"
                                            />
                                        ),
                                        asyncConfirmCallback: async ({
                                            selectedEmails,
                                            selectedPhoneNumbers,
                                            selectedMissionGroupIds,
                                        }) => {
                                            await shareLiveStream({
                                                type: "all-streams",
                                                selectedEmails,
                                                selectedPhoneNumbers,
                                                selectedMissionGroupIds,
                                            })
                                        },
                                    })
                                }}
                            />
                        )}
                    </div>
                    <div className="flex gap-2 items-center">
                        <div className="py-3">Password Protected</div>
                        <ToggleSwitch
                            isToggled={!!auth?.settings.passwordEnabled}
                            setIsToggled={(t) => {
                                if (!isEditor) return
                                handleUpdateSettings({
                                    passwordEnabled: t,
                                    password: localPassword,
                                })
                            }}
                            ballStyle="bg-blue-500"
                            className="scale-125"
                            disabled={isApplyingChanges}
                        />
                        {isEditor && !!auth?.settings.passwordEnabled && (
                            <>
                                <div className="ml-2">
                                    <PasswordInput
                                        value={localPassword}
                                        onChange={(e) => setLocalPassword(e.target.value)}
                                        disabled={isApplyingChanges}
                                    />
                                </div>
                                <ButtonPrimary
                                    title="Apply Password"
                                    onClick={() => {
                                        handleUpdateSettings({
                                            passwordEnabled: true,
                                            password: localPassword,
                                        })
                                    }}
                                    disabled={isApplyingChanges}
                                />
                            </>
                        )}
                    </div>
                    <div>
                        <ExpandableQRCode
                            title="All Streams"
                            size={110}
                            value={multistreamUrl({ workspaceId })}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
